import {faTrash} from '@fortawesome/pro-solid-svg-icons';
import {Box, CircularProgress, IconButton, Typography} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import {CustomButton} from 'commons/Button/CustomButton';
import {DialogType} from 'commons/Dialog/CustomDialog';
import CustomIcon from 'commons/Icon/CustomIcon';
import {localize} from 'commons/LocalizedText/LocalizedText';
import {CustomTable} from 'commons/Table/CustomTable';
import {CustomTooltip} from 'commons/Tooltip/CustomTooltip';
import {ProductTestUploadLaboReportDialog} from 'components/ProductTest/ProductTestUploadLaboReportDialog';
import {ProductTestUploadProviderBillDialog} from 'components/ProductTest/ProductTestUploadProviderBillDialog';
import SkeletonLaboratoryGrid from 'components/Skeletons/SkeleronLaboratoryGrid/SkeletonLaboratoryGrid';
import {useDialog} from 'hook/DialogProvider';
import React, {useState} from 'react';
import {generateRow} from 'utils/helpers/RowHelper';
import {timestampToDate} from 'utils/processing/dates';
import {EccFileDownload} from 'utils/rows/TestRows';
import {lowerCaseFirstLetter} from 'utils/processing/string';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '50px 0'
  },
  countryAutoComplete: {
    minWidth: '300px',
    margin: 0,
    '& .MuiTextField-root': {
      marginTop: 0
    }
  },
  programFileTab: {
    paddingTop: '40px'
  },
  generalPadding: {
    marginTop: theme.spacing(5)
  },
  tableContainer:{
    maxHeight: '25vh',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '12px'
    },
    '&::-webkit-scrollbar-track': {
      marginTop: '40px',
      backgroundColor: theme.palette.primary.light
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: theme.palette.primary.main
    }
  }
}));

export const ProductTestDocumentsPage = ({
  availableActions,
  handleDelete,
  handleUploadProductTestDocument,
  handleUploadProviderBillDocument,
  isLoadingApplications,
  isLoadingLaboReportGrid,
  isLoadingTestDetails,
  isLoadingProviderBill,
  isLoadingSave,
  productTestDocuments
}) => {
  const classes = useStyles();
  const showDialog = useDialog();
  const [isUploadReportDialogOpen, setUploadReportDialogOpen] = useState(false);
  const [isUploadInvoiceDialogOpen, setUploadInvoiceDialogOpen] = useState(false);

  const displayDeletionDialog = reportFile => {
    showDialog({
      variant: DialogType.CONFIRMATION,
      title: 'confirmAction',
      description: localize('deleteReportConfirmation'),
      onSubmit: () => {
        handleDelete(reportFile);
      }
    });
  }

  const productTestFilesRows = [
    generateRow({
      name: 'Document',
      width: '5%',
      mobileWidth: '20%',
      isSortable: false,
      alignItem: 'center',
      template: (row) => {
        return (
          <Grid
            alignItems="center"
            container
            direction="row"
            justifyContent="flex-start"
            spacing={1}

          >
            { row?.idCrypted && row?.type && (
              <Grid item>
                <EccFileDownload file={row}/>
              </Grid>
            )}
          </Grid>
        )}
    }),
    generateRow({
      name: 'type',
      width: '3%',
      mobileWidth: '10%',
      isSortable: false,
      template: (row) => (
        <Typography style={{overflow: 'hidden', textOverflow: 'ellipsis', flexWrap: 'nowrap'}} variant="body2">
          {row?.type?.text ?? row?.type?.id}
        </Typography>
      )
    })
  ];

  const productTestLaboratoryReportsRows = [
    generateRow({
      name: 'Document',
      width: '3%',
      mobileWidth: '10%',
      isSortable: false,
      template: (row) => {
        return (
          <Grid
            alignItems="center"
            container
            direction="row"
            justifyContent="flex-start"
            spacing={1}
            style={{overflow: 'hidden', textOverflow: 'ellipsis', flexWrap: 'nowrap'}}
          >
            { row?.reportFile?.idCrypted && row?.reportFile?.type && (
              <Grid item>
                <EccFileDownload file={row?.reportFile}/>
              </Grid>
            )}
          </Grid>
        )}
    }),
    generateRow({
      name: 'reportType',
      width: '8%',
      mobileWidth: '10%',
      isSortable: false,
      template: (row) => (

        <Typography variant="body2">
          <span style={{overflow: 'hidden', textOverflow: 'ellipsis', flexWrap: 'nowrap', wordBreak: 'unset'}}>
            {localize(lowerCaseFirstLetter(row?.reportFile?.type?.id))}
          </span>
        </Typography>


      )
    }),
    generateRow({
      name: 'uploadDate',
      width: '5%',
      mobileWidth: '10%',
      isSortable: false,
      template: (row) => (
        <Typography variant="body2">{timestampToDate(row?.reportFile?.creationDate)}</Typography>
      )
    }),
    generateRow({
      name: 'status',
      width: '3%',
      mobileWidth: '10%',
      isSortable: false,
      template: (row) => (
        <div>
          <Typography variant="body2">{localize(row?.reportFileStatusTrad)}</Typography>
        </div>
      )
    }),
    generateRow({
      name: 'actions',
      width: '3%',
      mobileWidth: '10%',
      isSortable: false,
      template: (row) => (
        <div>
          { row?.reportFile?.idCrypted
            && availableActions?.includes("DELETE_PRODUCT_TEST_REPORT")
            && row?.reportFile?.type?.id === "LabProductTestReport"
            && (
              <IconButton
                onClick={() => displayDeletionDialog(row?.reportFile)}
              >
                <CustomIcon
                  color="red"
                  icon={faTrash}
                  size="xs"
                />
              </IconButton>
            )}
        </div>
      )
    })
  ];

  const productTestOrderFormsRows = [
    generateRow({
      name: 'orderForm',
      width: '5%',
      mobileWidth: '20%',
      isSortable: false,
      template: (row) => {
        return (
          <Grid
            alignItems="center"
            container
            direction="row"
            justifyContent="flex-start"
            spacing={1}
            style={{overflow: 'hidden', textOverflow: 'ellipsis', flexWrap: 'nowrap'}}
          >
            { row?.labOrderFormFile?.idCrypted && row?.labOrderFormFile?.type && (
              <Grid item>
                <EccFileDownload file={row.labOrderFormFile}/>
              </Grid>
            )}
            <Grid item>
              <CustomTooltip title={`${row?.orderFormNumber}`}>
                <Typography variant="body2">{row?.orderFormNumber}</Typography>
              </CustomTooltip>
            </Grid>
          </Grid>
        )}
    }),
    generateRow({
      name: 'orderFormStatus',
      width: '5%',
      mobileWidth: '40%',
      isSortable: false,
      template: (row) => (
        <div>
          {row?.orderFormStatusTrad}
        </div>
      )
    }),
    generateRow({
      name: 'invoice',
      width: '5%',
      mobileWidth: '20%',
      isSortable: false,
      template: (row) => {
        return (
          <Grid
            alignItems="center"
            container
            direction="row"
            justifyContent="flex-start"
            spacing={1}
            style={{overflow: 'hidden', textOverflow: 'ellipsis', flexWrap: 'nowrap'}}
          >
            { row?.providerBillFile?.idCrypted && row?.providerBillFile.type && (
              <Grid item>
                <EccFileDownload file={row.providerBillFile}/>
              </Grid>
            )}
            <Grid item>
              <CustomTooltip title={`${row?.providerBillRef}`}>
                <Typography variant="body2">{row?.providerBillRef}</Typography>
              </CustomTooltip>
            </Grid>
          </Grid>
        )}
    }),
    generateRow({
      name: 'billAmount',
      label: 'amount',
      width: '5%',
      mobileWidth: '10%',
      isSortable: false,
      template: (row) => (
        <div>
          {row?.billAmount} €
        </div>
      )
    }),
    generateRow({
      name: 'emissionDate',
      width: '5%',
      mobileWidth: '20%',
      isSortable: false,
      template: (row) => (
        <Typography variant="body2">{timestampToDate(row?.providerBillEmissionDate)}</Typography>
      )
    }),
    generateRow({
      name: 'paymentApprovalDate',
      width: '5%',
      mobileWidth: '20%',
      isSortable: false,
      template: (row) => (
        <Typography variant="body2">{timestampToDate(row?.providerBillPaymentApprovalDate)}</Typography>
      )
    })
  ];


  return (
    (!isLoadingApplications && !isLoadingTestDetails && !isLoadingSave) ?
      <div className={classes.generalPadding}>
        <Grid className={classes.columnContainer} container direction='column' spacing={4}>
          <Grid item>
            <Grid alignItems="up" container direction="row" justifyContent="space-between" spacing={6} wrap='nowrap'>
              <Grid item xs={3}>
                <Grid container>
                  <Grid item>
                    <Typography style={{height: '40px'}} variant="h5">{localize('productFiles')}</Typography>
                  </Grid>
                  <Grid className={classes.tableContainer} item>
                    <CustomTable
                      headers={productTestFilesRows}
                      isBackend={false}
                      needForFooter={false}
                      rowKey="documentsRows"
                      rows={productTestDocuments?.programFiles}
                      stickyHeader
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={9}>
                <Grid item>
                  <ProductTestUploadLaboReportDialog
                    availableActions={availableActions}
                    onClose={() => setUploadReportDialogOpen(false)}
                    onSubmit={handleUploadProductTestDocument}
                    open={isUploadReportDialogOpen}
                  />
                  <Grid
                    alignItems="center"
                    container
                    direction="row"
                    justifyContent="space-between"
                    wrap='nowrap'
                  >
                    <Grid item>
                      <Typography variant="h5">{localize('laboratoryReports')}</Typography>
                    </Grid>
                    <Grid item>
                      <CustomButton
                        onClick={() => setUploadReportDialogOpen(true) }
                      >
                        {localize('addDocumentButton')}
                      </CustomButton>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid className={classes.tableContainer} item>
                  <CustomTable
                    headers={productTestLaboratoryReportsRows}
                    isBackend={false}
                    isLoading={isLoadingLaboReportGrid}
                    needForFooter={false}
                    rowKey="documentsRows"
                    rows={productTestDocuments?.reportsFiles}
                    stickyHeader
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid alignitem='center' container direction='rows'>
              <Grid item>
                <Grid className={classes.container} container direction='column' spacing={2}>
                  <Grid item>
                    <ProductTestUploadProviderBillDialog
                      onClose={() => setUploadInvoiceDialogOpen(false)}
                      onSubmit={handleUploadProviderBillDocument}
                      open={isUploadInvoiceDialogOpen}
                      orderForms={productTestDocuments?.orderForms}
                    />
                    <Box className={classes.form} style={{overflow: 'hidden'}}>
                      <Grid
                        alignItems="center"
                        container
                        direction="row"
                        justifyContent="space-between"
                        wrap='nowrap'
                      >
                        <Grid item>
                          <Typography variant="h5">{localize('laboratoryOrderFormsAndBills')}</Typography>
                        </Grid>
                        {!isLoadingProviderBill ?
                          <Grid item>
                            <CustomButton
                              onClick={() => setUploadInvoiceDialogOpen(true) }
                            >
                              {localize('addBillButton')}
                            </CustomButton>
                          </Grid>
                          :
                          <Grid item style={{width: '100px'}}>
                            <CircularProgress/>
                          </Grid>
                        }
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Grid  container direction='column' >
                      <CustomTable
                        headers={productTestOrderFormsRows}
                        rowKey="documentsRows"
                        rows={productTestDocuments?.orderForms}
                        stickyHeader
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      :
      <SkeletonLaboratoryGrid className={classes.container} />
  );
};